import React, { useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const OrganizationSettings = () => {
  const { currentOrganization, refreshOrganizations, isPrimaryFounder } = useAuth();
  const [orgName, setOrgName] = useState(currentOrganization?.organization.name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleUpdateOrganization = async () => {
    if (!currentOrganization || !orgName.trim()) return;
    
    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('organizations')
        .update({ name: orgName })
        .eq('id', currentOrganization.organization.id);
      
      if (error) throw error;
      
      toast.success('Organization updated successfully');
      await refreshOrganizations();
    } catch (error: any) {
      toast.error(`Error updating organization: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!currentOrganization) return;
    if (deleteConfirmation !== currentOrganization.organization.name) return;
    
    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', currentOrganization.organization.id);
      
      if (error) throw error;
      
      toast.success('Organization deleted successfully');
      setIsDeleteDialogOpen(false);
      await refreshOrganizations();
    } catch (error: any) {
      toast.error(`Error deleting organization: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentOrganization) {
    return (
      <div className="text-center p-8">
        <p>No organization selected</p>
      </div>
    );
  }

  if (!isPrimaryFounder()) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">Only the primary founder can change organization settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization Details</CardTitle>
          <CardDescription>Update your organization information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Enter organization name"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleUpdateOrganization} 
            disabled={isSubmitting || !orgName.trim() || orgName === currentOrganization.organization.name}
          >
            {isSubmitting ? 'Updating...' : 'Update Organization'}
          </Button>
        </CardFooter>
      </Card>

      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Actions here cannot be undone. Please be certain.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Deleting your organization will remove all associated data including departments, 
            projects, documents, and team member associations. This action is permanent.
          </p>
        </CardContent>
        <CardFooter>
          <Button 
            variant="destructive" 
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            Delete Organization
          </Button>
        </CardFooter>
      </Card>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your organization
              and all associated data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-4">
            <Label htmlFor="delete-confirmation">
              Type <span className="font-medium">{currentOrganization.organization.name}</span> to confirm
            </Label>
            <Input
              id="delete-confirmation"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Organization name"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOrganization}
              disabled={deleteConfirmation !== currentOrganization.organization.name || isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting ? 'Deleting...' : 'Delete Organization'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
